import { useState, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import ConfigData from "./config.json"; 
import { setDatasetsJson } from "./utils/DatasetsJson";
import L from "leaflet";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import ReactDOM from "react-dom/client";

const Panels = () => {
  const map = useMap();
  const containerRightRef = useRef(null);
  const containerLeftRef = useRef(null);
  const rightRootRef = useRef(null);
  const leftRootRef = useRef(null);
  const [datasetsLoaded, setDatasetsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isMobile, setIsMobile] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    fetch(ConfigData.GetDatasets)
    .then(response => {
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      return response.json();
    })
    .then(data => {
      setDatasetsJson(data);
      setDatasetsLoaded(true);
    })
    .catch(err => {
      console.error("Error loading datasets:", err);
      setError(true);
    });

    const checkIfMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(prev => (prev !== mobile ? mobile : prev));
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (!map || mountedRef.current) return;

    const existingLeft = document.querySelector(".leaflet-top.leaflet-left .custom-control-panel");
    const existingRight = document.querySelector(".leaflet-top.leaflet-right .custom-control-panel");

    if (!existingLeft) {
      containerLeftRef.current = L.DomUtil.create("div", "custom-control-panel");
      const controlLeft = L.control({ position: "topleft" });
      controlLeft.onAdd = () => containerLeftRef.current;
      controlLeft.addTo(map);
    }

    if (!existingRight) {
      containerRightRef.current = L.DomUtil.create("div", "custom-control-panel");
      const controlRight = L.control({ position: "topright" });
      controlRight.onAdd = () => containerRightRef.current;
      controlRight.addTo(map);
    }

    mountedRef.current = true;
    map.invalidateSize();
  }, [map]);

  useEffect(() => {
    if (!containerLeftRef.current || !containerRightRef.current || !datasetsLoaded || error || isMobile === null) return;

    if (!leftRootRef.current) {
      leftRootRef.current = ReactDOM.createRoot(containerLeftRef.current);
    }
    if (!rightRootRef.current) {
      rightRootRef.current = ReactDOM.createRoot(containerRightRef.current);
    }

    leftRootRef.current.render(
      <LeftPanel map={map} isMobile={isMobile} setActivePanel={setActivePanel} />
    );
    rightRootRef.current.render(
      <RightPanel map={map} isMobile={isMobile} setActivePanel={setActivePanel} />
    );
  }, [datasetsLoaded, error, isMobile, map]);

  useEffect(() => {
    if (!map) return;

    const updateUrl = () => {
      const zoom = map.getZoom();
      const center = map.getCenter();
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      params.set("zoom", zoom);
      params.set("lat", center.lat.toFixed(6));
      params.set("lng", center.lng.toFixed(6));

      const newUrl = url.pathname + "?" + params.toString();
      window.history.replaceState({ path: newUrl }, "", newUrl);
    };

    map.on("moveend", updateUrl);
    map.on("zoomend", updateUrl);

    return () => {
      map.off("moveend", updateUrl);
      map.off("zoomend", updateUrl);
    };
  }, [map]);

  useEffect(() => {
    if (!isMobile || !containerLeftRef.current || !containerRightRef.current) return;

    const leftParent = containerLeftRef.current.parentElement;
    const rightParent = containerRightRef.current.parentElement;

    if (activePanel === "left") {
      leftParent.style.zIndex = "1001";
      rightParent.style.zIndex = "";
    } else if (activePanel === "right") {
      leftParent.style.zIndex = "";
      rightParent.style.zIndex = "1001";
    } else {
      leftParent.style.zIndex = "";
      rightParent.style.zIndex = "";
    }
  }, [activePanel, isMobile]);

  useEffect(() => {
    if (!map || !error) return;

    const container = L.DomUtil.create("div", "map-error");
    container.innerText = "Unable to load datasets. Refresh the page to try again.";

    map.getContainer().appendChild(container);

    return () => map.getContainer().removeChild(container);
  }, [map, error]);

  return null;
};

export default Panels;
